
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { getOperatingSystem } from '../utils/get-operating-system'
import { debounce, throttle } from 'throttle-debounce'

const KonfektPage1 = {
  init(): void {
    if (!document.querySelector('.konfekt-page.konfekt-page--1')) {
      return
    }

    const start = () => {
      gsap.registerPlugin(ScrollTrigger)
    
      this.handleAnimations()
      this.handleCardHovers()
      this.handleParallaxes()
      this.handleScrollUp()
      ScrollTrigger.refresh()
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        start()
      }, { once: true })
    } else {
      start()
    }

    window.addEventListener('load', () => {
      ScrollTrigger.refresh()
    }, { once: true })
  },

  handleAnimations(): void {
    const refreshInfoTextTriggers = debounce(120, () => {
      ScrollTrigger.getAll()
        .filter((trigger) => trigger.vars.id === 'info-text-item')
        .forEach((trigger) => {
          trigger.refresh()
        })
    })

    gsap.to('.kp-rays svg', { rotation: 360, duration: 123, ease: 'none', repeat: -1 })

    // Intro animation

    gsap
      .timeline()
      // .set('html', { overflow: 'hidden' })
      .to('.kp-cover > div', { duration: 0.7, ease: 'power2.inOut', x: 0 }, 0.2)
      .set('.kp-container', { visibility: 'visible' })
      .to('.kp-cover > div', { duration: 0.7, ease: 'power2.inOut', xPercent: 100 })
      .set('.kp-cover', { display: 'none' })
      // .set('html', { clearProps: 'overflow', onComplete: ScrollTrigger.refresh }, '<-0.21')

    // Reveal hero title and supertitle

    const heroRevealStart = 0.44

    gsap
      .timeline()
      .set('.kp-header__logo', { filter: 'blur(8rem)', opacity: 0 })
      .set('.kp-hero__title .kp-title__mask', { xPercent: -100 })
      .set('.kp-hero__supertitle', { filter: 'blur(8rem)', opacity: 0 })
      .set('.kp-candy--hero', { yPercent: -10 })
      .to('.kp-hero__title .kp-title__overlay', { duration: 2, ease: 'power2.inOut', xPercent: 100 }, heroRevealStart)
      .to('.kp-hero__title .kp-title__mask', { duration: 4, ease: 'power2.out', xPercent: 50 }, '>-1.4')
      .to('.kp-hero__supertitle', { duration: 2, filter: 'blur(0rem)', opacity: 1 }, '<+0.4')
      .to('.kp-header__logo', { duration: 2, filter: 'blur(0rem)', opacity: 1 }, '<+0.2')
      .to('.kp-candy--hero', { duration: 3, ease: 'power2.out', yPercent: 0 }, heroRevealStart)

    // Info section fade in on scroll

    gsap.set('.kp-info__inner', { opacity: 0, y: '123rem', overwrite: true })

    ScrollTrigger.create({
      onEnter: () => {
        gsap.to('.kp-info__inner', {
          duration: 1.23,
          ease: 'power3.out',
          opacity: 1,
          y: '0rem',
          onComplete: refreshInfoTextTriggers,
        })
      },
      start: '0% 80%',
      trigger: '.kp-info',
    })

    ScrollTrigger.create({
      onLeaveBack: () => {
        gsap.set('.kp-info__inner', { opacity: 0, y: '123rem', overwrite: true })
      },
      start: '0% 100%',
      trigger: '.kp-info',
    })

    // Info text list items animation on scroll

    document.querySelectorAll('.kp-info__text > p').forEach((item, index) => {
      gsap.set(item, { opacity: 0, overwrite: true })
    
      ScrollTrigger.create({
        id: 'info-text-item',
        onEnter: () => {
          gsap.to(item, { duration: 1.234, ease: 'power3.out', opacity: 1, delay: index * 0.1 })
        },
        start: '0% 90%',
        trigger: item,
      })

      ScrollTrigger.create({
        id: 'info-text-item',
        onLeaveBack: () => {
          gsap.set(item, { opacity: 0, overwrite: true })
        },
        start: '0% 100%',
        trigger: item,
      })
    })

    document.querySelectorAll('.kp-info__text li').forEach((item, index) => {
      item.insertAdjacentHTML('afterbegin', '<div><div></div></div>')

      gsap.set(item, { opacity: 0, overwrite: true, x: '44rem' })
      gsap.set(item.firstElementChild, { overwrite: true, scale: 5, x: '-44rem' })
    
      ScrollTrigger.create({
        id: 'info-text-item',
        onEnter: () => {
          gsap.to(item, { delay: index * 0.1, duration: 1.234, ease: 'power3.out', opacity: 1, x: 0 })
          gsap.to(item.firstElementChild, { delay: index * 0.1, duration: 1.234, ease: 'power3.out', scale: 1, x: 0 })
        },
        start: '0% 90%',
        trigger: item,
      })

      ScrollTrigger.create({
        id: 'info-text-item',
        onLeaveBack: () => {
          gsap.set(item, { opacity: 0, overwrite: true, x: '44rem' })
          gsap.set(item.firstElementChild, { overwrite: true, scale: 5, x: '-44rem' })
        },
        start: '0% 100%',
        trigger: item,
      })
    })

    // Info image zoom in on scroll

    // gsap.matchMedia().add({
    //   isDesktop: `(min-width: 768px)`,
    //   isMobile: `(max-width: 767px)`,
    // }, (context) => {
    //   const { isDesktop } = context.conditions

    //   ScrollTrigger.create({
    //     animation: gsap.fromTo('.kp-info__picture', { scale: isDesktop ? 1.66 : 2.66 }, { ease: 'none', scale: 1 }),
    //     end: isDesktop ? '85% 100%' : '100% 100%',
    //     start: '0% 100%',
    //     scrub: 1.21,
    //     trigger: '.kp-info',
    //   })
    // })

    // Details section fade in on scroll

    gsap.set('.kp-details__inner', { opacity: 0, y: '123rem', overwrite: true })

    ScrollTrigger.create({
      onEnter: () => {
        gsap.to('.kp-details__inner', { duration: 1.23, ease: 'power3.out', opacity: 1, y: '0rem' })
      },
      start: '0% 80%',
      trigger: '.kp-details',
    })

    ScrollTrigger.create({
      onLeaveBack: () => {
        gsap.set('.kp-details__inner', { opacity: 0, y: '123rem', overwrite: true })
      },
      start: '0% 100%',
      trigger: '.kp-details',
    })

    // Products title animation on scroll

    gsap
      .timeline()
      .set('.kp-products__title .kp-title__mask', { xPercent: -100, overwrite: true })
      .set('.kp-products__title .kp-title__overlay', { xPercent: 0, overwrite: true })

    ScrollTrigger.create({
      animation: gsap
        .timeline()
        .set('.kp-products__title .kp-title__mask', { xPercent: -100 })
        .to('.kp-products__title .kp-title__overlay', { duration: 2, ease: 'none', xPercent: 100 })
        .to('.kp-products__title .kp-title__mask', { duration: 4, ease: 'none', xPercent: 70 }, '>-1.5'),
      end: '0% 5%',
      start: '0% 100%',
      scrub: 1.21,
      trigger: '.kp-products__title',
    })

    // Products cards animation on scroll

    gsap.matchMedia().add({
      isDesktop: `(min-width: 768px)`,
      isMobile: `(max-width: 767px)`,
    }, (context) => {
      const { isDesktop } = context.conditions

      document.querySelectorAll('.kp-products__card-inner').forEach((card, index) => {
        gsap.set(card, { opacity: 0, y: '123rem' })

        ScrollTrigger.create({
          onEnter: () => {
            const delay = isDesktop ? (index % 4) * 0.1 : (index % 2) * 0.1
            gsap.to(card, { delay,  duration: 1.23, ease: 'power3.out', opacity: 1, y: '0rem' })
          },
          start: '0% 80%',
          trigger: card.parentElement,
        })

        ScrollTrigger.create({
          onLeaveBack: () => {
            gsap.set(card, { opacity: 0, overwrite: true, y: '123rem' })
          },
          start: '0% 100%',
          trigger: card.parentElement,
        })
      })
    })

    // Footer logo animation on scroll

    ScrollTrigger.create({
      animation: gsap
        .timeline()
        .set('.kp-footer__title .kp-title__mask', { xPercent: -100 })
        .to('.kp-footer__title .kp-title__overlay', { duration: 2, ease: 'none', xPercent: 100 })
        .to('.kp-footer__title .kp-title__mask', { duration: 4, ease: 'power2.out', xPercent: 50 }, '>-1.5'),
      end: '0% 5%',
      start: '0% 100%',
      scrub: 1.21,
      trigger: '.kp-footer__title',
    })

    // Footer buttons animation on scroll

    gsap.set('.kp-footer__buttons .kp-button', { opacity: 0, overwrite: true })

    ScrollTrigger.create({
      onEnter: () => {
        gsap.to('.kp-footer__buttons .kp-button', { duration: 2.1, ease: 'power3.out', opacity: 1, stagger: 0.1 })
      },
      start: '100% 110%',
      trigger: '.kp-footer',
    })

    ScrollTrigger.create({
      onLeaveBack: () => {
        gsap.set('.kp-footer__buttons .kp-button', { opacity: 0, overwrite: true })
      },
      start: '0% 100%',
      trigger: '.kp-footer__buttons',
    })
  },

  handleCardHovers(): void {
    if (getOperatingSystem() !== 'desktop') return

    // Moving product cards

    const commonProps = {
      duration: 1.23,
      ease: 'power3.out',
      overwrite: true,
    }

    const moveTheCard = (event, card, rotationModifier = 0.21) => {
      const rect = card.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      const xPercent = ((x / rect.width) * 100) - 50
      const yPercent = ((y / rect.height) * 100) - 50

      gsap.to(card, {
        ...commonProps,
        rotateX: -yPercent * rotationModifier,
        rotateY: xPercent * rotationModifier,
      })

      gsap.to(card.querySelector('img'), {
        ...commonProps,
        x: `${xPercent / -6.3}rem`,
        y: `${yPercent / -4.2}rem`,
      })
    }
    
    document.querySelectorAll('.kp-products__card-inner-2').forEach((card) => {
      card.addEventListener('mouseleave', debounce(70, () => {
        gsap.to(card, { ...commonProps, rotateX: 0, rotateY: 0 },)
        gsap.to(card.querySelector('img'), { ...commonProps, x: '0rem', y: '0rem' })
      }))

      card.addEventListener('mousemove', throttle(60, (e: MouseEvent) => {
        moveTheCard(e, card)
      }))
    })

    // Moving info & details card

    document.body.addEventListener('mousemove', throttle(60, (e: MouseEvent) => {
      const x = e.clientX
      const y = e.clientY
      const xPercent = ((x / window.innerWidth) * 100) - 50
      const yPercent = ((y / window.innerHeight) * 100) - 50
      const rotationModifier = 0.021

      gsap.to('.kp-details__inner-2, .kp-info__inner-2', {
        ...commonProps,
        rotateX: -yPercent * rotationModifier,
        rotateY: xPercent * rotationModifier,
      })

      gsap.to('.kp-details__picture img', {
        ...commonProps,
        duration: 2.1,
        x: `${xPercent / 1.7}rem`,
      })

      gsap.to('.kp-info__picture img', {
        ...commonProps,
        duration: 2.1,
        x: `${xPercent / 3.2}rem`,
      })
    }))
  },

  handleParallaxes(): void {
    // Set parallax amounts

    const setParallax = (block, child, parallax) => {
      document.querySelector<HTMLElement>(`.kp-candy--${block} > div:nth-child(${child})`).dataset.parallax = parallax
    }

    setParallax('hero', 1, 121)
    setParallax('hero', 2, 90)
    setParallax('hero', 3, 160)

    setParallax('products-1', 1, 121)
    setParallax('products-1', 2, 210)

    setParallax('products-2', 1, 121)
    setParallax('products-2', 2, 210)
    setParallax('products-2', 3, 121)
    setParallax('products-2', 4, 150)

    setParallax('footer', 1, 180)
    setParallax('footer', 2, 121)
    setParallax('footer', 3, 210)

    // Handle animation on scroll

    const candies = document.querySelectorAll<HTMLElement>('.kp-candy > div')

    gsap.matchMedia().add({
      isDesktop: `(min-width: 768px)`,
      isMobile: `(max-width: 767px)`,
    }, (context) => {
      const { isDesktop } = context.conditions
  
      candies.forEach((candy) => {
        const parallax = candy.dataset.parallax ? parseInt(candy.dataset.parallax, 10) : 121
        const rotation = gsap.utils.random(6, 12, 1)
        const rotationDirection = gsap.utils.random(0, 1, 1) ? 1 : -1
        const rotationModifier = isDesktop ? 1 : 2.1
  
        ScrollTrigger.create({
          animation: gsap.fromTo(candy, {
            rotation: -rotation * rotationDirection * rotationModifier,
          }, {
            ease: 'none',
            rotation: rotation * rotationDirection * rotationModifier,
            yPercent: -parallax,
          }),
          end: '100% 0%',
          scrub: parallax / 100, // 1.21,
          start: '0% 100%',
          trigger: candy,
        })
      })
    })
    
    // Handle mousemove

    document.body.addEventListener('mousemove', throttle(60, (e: MouseEvent) => {
      if (getOperatingSystem() !== 'desktop') return

      const x = e.clientX
      const y = e.clientY
      const xPercent = ((x / window.innerWidth) * 100) - 50
      const yPercent = ((y / window.innerHeight) * 100) - 50

      candies.forEach((candy) => {
        const transformModifier = gsap.utils.random(2.5, 3.5, 0.1)

        gsap.to(candy.querySelector('picture'), {
          duration: 3.45,
          ease: 'power3.out',
          overwrite: true,
          rotation: -xPercent / 10,
          x: `${-xPercent / transformModifier}rem`,
          y: `${-yPercent / transformModifier}rem`,
        })
      })
    }))
  },

  handleScrollUp(): void {
    document.querySelector('.kp-button--scroll-up').addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    })
  },
}

export default KonfektPage1
